import React from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaBrain, FaLungs, FaBone, FaCircle, FaLeaf, FaHandsHelping, FaSun } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import bioresImage from '../../components/assets/Staff/Biores 2.jpg';
import nlsImage from '../../components/assets/Staff/NSL 1.jpg';
import hydroImage from '../../components/assets/Staff/Hydro.jpg';
import trTherapyImage from '../../components/assets/Staff/TR Therapy 2.jpg';
import bemerImage from '../../components/assets/Staff/Bemer 2.jpg';
import irTherapyImage from '../../components/assets/Staff/IR Therapy 2.jpg';
import cuppingImage from '../../components/assets/Staff/Cupping 1.png';
import swedishImage from '../../components/assets/Staff/Swedish 1.jpg';

const Therapy = () => {
  const therapies = [
    { id: 1, name: 'Bioresonance Therapy', path: '/therapy/bioresonance-therapy', description: 'Comprehensive therapy with 7 specialized sub-therapies.', icon: FaLeaf, hasSubTherapies: true, image: bioresImage },
    { id: 2, name: 'NLS Hunter', path: '/therapy/nls-hunter', description: 'Advanced NLS Hunter therapy for comprehensive health analysis and therapy.', icon: FaHeart, image: nlsImage },
    { id: 3, name: 'HydroVitality', path: '/therapy/hydro-vitality', description: 'Specialized hydrotherapy for enhanced vitality and wellness.', icon: FaLungs, image: hydroImage },
    { id: 4, name: 'Targeted Radio Frequency', path: '/therapy/tr-therapy', description: 'A new standard of comfort, warmth and body awareness using advanced radiofrequency technology.', icon: FaSun, image: trTherapyImage },
    { id: 5, name: 'BEMER Sessions', path: '/therapy/bemer-sessions', description: 'Gentle support for your body\'s natural rhythm using patented European technology for microcirculation.', icon: FaHandsHelping, image: bemerImage },
    { id: 6, name: 'Red Light Sessions with Helios', path: '/therapy/red-light-sessions', description: 'Revolutionary red light therapy sessions using Helios technology.', icon: FaBrain, image: irTherapyImage },
    { id: 7, name: 'Cupping Therapy', path: '/therapy/cupping-therapy', description: 'Traditional healing practice using gentle suction to promote circulation.', icon: FaCircle, image: cuppingImage },
    { id: 8, name: 'Swedish Massage', path: '/therapy/swedish-massage', description: 'Traditional Swedish massage therapy for relaxation and muscle tension relief.', icon: FaBone, image: swedishImage }
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO
        title="Our Services"
        description="Holistic therapies at Bioresonance Leicester: bioresonance, NLS Hunter, HydroVitality, BEMER, red light therapy, cupping, Swedish massage. Book at 108 New Walk, Leicester."
        path="/therapy"
      />
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
              Our Services
            </h1>
            <p className="text-xl text-dark-grey mb-4 leading-relaxed max-w-3xl mx-auto">
              Discover our comprehensive range of holistic therapies designed to support your health and wellbeing journey.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Each therapy is carefully selected and administered by our experienced practitioners to provide personalized, effective care.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {therapies.map((therapy, index) => (
              <motion.div
                key={therapy.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative"
              >
                <Link to={therapy.path} className="block">
                  <div className="relative">
                    {/* Main card container - same photo style as home page */}
                    <div className="relative p-4">
                      <div className="relative h-80 rounded-2xl overflow-hidden border border-grey/20 bg-white shadow-lg hover:shadow-xl transition-all duration-500 hover:border-gold/50">
                        {/* Background Image - same as home page */}
                        <img
                          src={therapy.image}
                          alt={therapy.name}
                          className="absolute inset-0 w-full h-full object-cover opacity-80 rounded-2xl z-0"
                        />
                        {/* Treatment Name at Bottom - solid bar, no blur */}
                        <div className="absolute bottom-0 left-0 right-0 bg-white/95 rounded-b-2xl p-4 z-10 border-t border-grey/10">
                          <h2 className="text-xl font-semibold text-dark-grey text-center group-hover:text-gold transition-colors duration-300">
                            {therapy.name}
                            {therapy.hasSubTherapies && (
                              <span className="ml-2 text-sm text-gold font-normal">(7 sub-therapies)</span>
                            )}
                          </h2>
                        </div>
                      </div>
                      
                      {/* Creative border */}
                      <div className="absolute inset-0 border-4 border-gold/30 rounded-2xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                      <div className="absolute inset-2 border-2 border-white/50 rounded-2xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                    </div>
                    
                    {/* Floating elements */}
                    <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                    <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Therapy;
